/**
 * Lista de projetos: busca, filtros combináveis (ficam no endereço, dá para mandar o link), ordenação por coluna,
 * filtros salvos (pessoais ou do time) e exportação para Excel (CSV).
 */
import { useMemo, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import clsx from 'clsx';
import { DASHBOARD_STATUSES, PHASE_LABEL, PHASES, PROJECT_ROLE_PROFILE, type DashboardStatus } from '@pmis/shared';
import { api, downloadFile, errorMessage } from '../lib/api';
import { useAuth } from '../lib/auth';
import { useLookups, withProfile } from '../lib/queries';
import { appLink, appPath } from '../lib/router';
import type { Project } from '../lib/types';
import { projectColumns } from '../lib/projectColumns';
import { ColumnsTable } from '../components/ColumnsTable';
import { Button, Field, Input, Select, Spinner } from '../components/ui';
import { Empty, Icon, PageHeader, StatusBadge, useToast } from '../components/pmis';


export function ProjectsPage() {
  const [sp, setSp] = useSearchParams();
  const nav = useNavigate();
  const { me } = useAuth();
  const toast = useToast();
  const qc = useQueryClient();
  const lookups = useLookups();
  const [q, setQ] = useState(sp.get('q') ?? '');
  const filters = Object.fromEntries(sp.entries());
  const set = (k: string, v: string | null) => { const n = new URLSearchParams(sp); if (v) n.set(k, v); else n.delete(k); setSp(n, { replace: true }); };
  const query = {
    q: filters.q, status: filters.status, phase: filters.fase, pm: filters.gerente, tech: filters.tecnico, client: filters.cliente, category: filters.categoria,
    vendor: filters.fabricante, from: filters.de, to: filters.ate, mine: filters.meus, sort: filters.ordem ?? 'kickoff', dir: filters.dir ?? 'desc',
    active: filters.ativos === 'todos' || filters.ativos === 'arquivados' ? 'all' : filters.ativos === 'encerrados' ? '0' : filters.status || filters.fase ? undefined : '1',
    archived: filters.ativos === 'arquivados' ? '1' : undefined,
  };
  const list = useQuery({ queryKey: ['projects', query], queryFn: () => api<{ total: number; items: Project[] }>('/api/v1/projects', { query }), placeholderData: (p) => p });
  const views = useQuery({ queryKey: ['views'], queryFn: () => api<{ id: number; name: string; filters: Record<string, string>; shared: boolean; mine: boolean; owner: string }[]>('/api/v1/me/views') });
  const statuses = (filters.status ?? '').split(',').filter(Boolean);
  const toggleStatus = (s: DashboardStatus) => set('status', (statuses.includes(s) ? statuses.filter((x) => x !== s) : [...statuses, s]).join(',') || null);
  const sortBy = (k: string) => { const n = new URLSearchParams(sp); n.set('ordem', k); n.set('dir', filters.ordem === k && filters.dir !== 'asc' ? 'asc' : 'desc'); setSp(n, { replace: true }); };
  // Gerente de projetos e engenheiro de campo: só quem tem o perfil (o filtro escolhido continua na lista).
  const pms = useMemo(() => withProfile(lookups.data?.users, PROJECT_ROLE_PROFILE.pm, { keep: filters.gerente }), [lookups.data, filters.gerente]);
  const techs = useMemo(() => withProfile(lookups.data?.users, PROJECT_ROLE_PROFILE.tech, { keep: filters.tecnico }), [lookups.data, filters.tecnico]);
  const items = list.data?.items ?? [];
  const showFin = items.some((p) => p.financial);
  const cols = useMemo(() => projectColumns({ financial: showFin }), [showFin]);
  return (
    <div className="space-y-4">
      <PageHeader title="Projetos" subtitle={list.data ? `${list.data.total} projeto(s) neste filtro` : undefined}
        actions={<>
          <Button onClick={() => downloadFile('/api/v1/projects/export.csv', 'projetos-pmis.csv', query as Record<string, string | undefined>).catch((e) => toast(errorMessage(e), 'err'))}>
            <Icon name="download" className="size-4" />Exportar (Excel)
          </Button>
          {me?.permissions['project.create'] ? <Button variant="primary" onClick={() => nav('/projetos/novo')}><Icon name="plus" className="size-4" />Novo projeto</Button> : null}
        </>} />

      <div className="space-y-3 rounded-xl border border-line bg-panel/80 p-3">
        <div className="flex flex-wrap items-end gap-3">
          <form className="min-w-64 flex-1" onSubmit={(e) => { e.preventDefault(); set('q', q.trim() || null); }}>
            <Field label="Buscar"><Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Nome, cliente, OP, TAP, gerente ou técnico — Enter para buscar" /></Field>
          </form>
          <Field label="Situação" className="w-40">
            <Select value={filters.ativos ?? ''} onChange={(e) => set('ativos', e.target.value || null)}>
              <option value="">Ativos</option><option value="encerrados">Encerrados</option><option value="todos">Todos</option>
              {me?.permissions['project.archive'] || filters.ativos === 'arquivados' ? <option value="arquivados">Arquivados</option> : null}
            </Select>
          </Field>
          <Field label="Fase" className="w-44"><Select value={filters.fase ?? ''} onChange={(e) => set('fase', e.target.value || null)}><option value="">Todas</option>
            {PHASES.map((p) => <option key={p} value={p}>{PHASE_LABEL[p]}</option>)}</Select></Field>
          <Field label="Gerente de projetos" className="w-44"><Select value={filters.gerente ?? ''} onChange={(e) => set('gerente', e.target.value || null)}><option value="">Todos</option>
            {pms.map((u) => <option key={u.id} value={u.id}>{u.name}</option>)}</Select></Field>
          <Field label="Eng. de campo" className="w-44"><Select value={filters.tecnico ?? ''} onChange={(e) => set('tecnico', e.target.value || null)}><option value="">Todos</option>
            {techs.map((u) => <option key={u.id} value={u.id}>{u.name}</option>)}</Select></Field>
          <Field label="Cliente" className="w-48"><Select value={filters.cliente ?? ''} onChange={(e) => set('cliente', e.target.value || null)}><option value="">Todos</option>
            {(lookups.data?.clients ?? []).map((c) => <option key={c.id} value={c.id}>{c.code} · {c.name}</option>)}</Select></Field>
          <Field label="Categoria" className="w-44"><Select value={filters.categoria ?? ''} onChange={(e) => set('categoria', e.target.value || null)}><option value="">Todas</option>
            {(lookups.data?.categories ?? []).map((c) => <option key={c.id} value={c.id}>{c.code} · {c.name}</option>)}</Select></Field>
          <Field label="Fabricante" className="w-40"><Select value={filters.fabricante ?? ''} onChange={(e) => set('fabricante', e.target.value || null)}><option value="">Todos</option>
            {(lookups.data?.partners ?? []).map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}</Select></Field>
          <Field label="Kickoff de" className="w-36"><Input type="date" value={filters.de ?? ''} onChange={(e) => set('de', e.target.value || null)} /></Field>
          <Field label="até" className="w-36"><Input type="date" value={filters.ate ?? ''} onChange={(e) => set('ate', e.target.value || null)} /></Field>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <span className="text-xs text-muted">Status:</span>
          {DASHBOARD_STATUSES.map((s) => (
            <button key={s} type="button" onClick={() => toggleStatus(s)} aria-pressed={statuses.includes(s)}
              className={clsx('rounded-full transition', statuses.includes(s) ? 'ring-2 ring-accent' : 'opacity-80 hover:opacity-100')}>
              <StatusBadge status={s} />
            </button>
          ))}
          <label className="ml-2 inline-flex items-center gap-1.5 text-xs"><input type="checkbox" checked={filters.meus === '1'} onChange={(e) => set('meus', e.target.checked ? '1' : null)} />Só os meus</label>
          <span className="mx-1 h-4 w-px bg-line" />
          <Select className="h-8 w-56 text-xs" value="" aria-label="Filtros salvos" onChange={(e) => {
            const v = views.data?.find((x) => String(x.id) === e.target.value);
            if (v) { setSp(new URLSearchParams(v.filters), { replace: true }); setQ(v.filters.q ?? ''); }
          }}>
            <option value="">Filtros salvos…</option>
            {(views.data ?? []).map((v) => <option key={v.id} value={v.id}>{v.name}{v.shared ? ` (time${v.mine ? '' : ` · ${v.owner}`})` : ''}</option>)}
          </Select>
          <Button size="sm" variant="ghost" onClick={async () => {
            const name = window.prompt('Nome do filtro (ex.: "FAST ativos da Gabi")');
            if (!name) return;
            const shared = window.confirm('Compartilhar com o time? (OK = sim, Cancelar = só para você)');
            try { await api('/api/v1/me/views', { method: 'POST', body: { page: 'projects', name, filters, shared } }); await qc.invalidateQueries({ queryKey: ['views'] }); toast('Filtro salvo.'); } catch (err) { toast(errorMessage(err), 'err'); }
          }}>Salvar filtro</Button>
          <Button size="sm" variant="ghost" title="Copia o link desta lista, com os filtros (a barra de endereço mostra só o domínio)" onClick={() => {
            const url = appLink(appPath());
            navigator.clipboard.writeText(url).then(() => toast('Link da lista copiado.', 'ok'), () => window.prompt('Copie o link:', url));
          }}><Icon name="link" className="size-4" />Copiar link</Button>
          {Object.keys(filters).length ? <Button size="sm" variant="ghost" onClick={() => { setSp(new URLSearchParams(), { replace: true }); setQ(''); }}>Limpar</Button> : null}
        </div>
      </div>

      {list.isLoading ? <Spinner /> : items.length === 0 ? <Empty title="Nenhum projeto encontrado">Mude os filtros ou a busca.</Empty> : (
        <div className={clsx('table-scroll max-h-[calc(100vh-330px)] rounded-xl border border-line bg-panel transition-opacity', list.isFetching && 'opacity-70')}>
          <ColumnsTable id="projetos" className="w-full text-sm">
            <thead className="text-left text-xs text-muted">
              <tr>
                {cols.map((c) => (
                  <th key={c.key} data-col={c.extra ? 'extra' : undefined} data-col-label={c.label}
                    className={clsx('whitespace-nowrap px-3 py-2.5 font-medium', c.align === 'right' && 'text-right', c.className)}>
                    {c.sort ? (
                      <button type="button" onClick={() => sortBy(c.sort!)} className="inline-flex items-center gap-1 hover:text-fg">
                        {c.label}{(filters.ordem ?? 'kickoff') === c.sort ? <span aria-hidden>{(filters.dir ?? 'desc') === 'asc' ? '▲' : '▼'}</span> : null}
                      </button>
                    ) : c.label}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {items.map((p) => (
                <tr key={p.id} className="border-t border-line hover:bg-panel-2/60">
                  {cols.map((c) => (
                    <td key={c.key} className={clsx('px-3 py-2', c.align === 'right' && 'num text-right', c.nowrap && 'whitespace-nowrap', c.className)}>{c.render(p)}</td>
                  ))}
                </tr>
              ))}
            </tbody>
          </ColumnsTable>
        </div>
      )}
      <p className="text-xs text-muted">Dica: o endereço desta página guarda os filtros, então dá para mandar o link para alguém do time. Status são combináveis (clique em mais de um).</p>
    </div>
  );
}
